import {
	ReactExternalControlBarProExtension,
	ReactExternalEditorProExtension,
	ReactExternalToolboxProExtension,
	useExternalControlBarController,
	useExternalToolboxController
} from '@nocode-js/sequential-workflow-designer-react-pro';
import { useMemo, useState, MouseEvent, TouchEvent, useEffect } from 'react';
import { Definition, DesignerExtension, Step, Uid } from 'sequential-workflow-designer';
import { SequentialWorkflowDesigner, wrapDefinition } from 'sequential-workflow-designer-react';
import { ExternalRootEditor } from './ExternalRootEditor';
import { ExternalStepEditor } from './ExternalStepEditor';

function createStep(): Step {
	return {
		componentType: 'task',
		id: Uid.next(),
		name: 'Test',
		properties: {},
		type: 'task'
	};
}

const startDefinition: Definition = {
	properties: {},
	sequence: [createStep()]
};

export function ExternalUiComponents() {
	const externalControlBarController = useExternalControlBarController();
	const externalToolboxController = useExternalToolboxController();
	const [externalEditorPlaceholder, setExternalEditorPlaceholder] = useState<HTMLElement | null>(null);
	const [isVisible, setIsVisible] = useState(true);
	const [isDragDisabled, setIsDragDisabled] = useState(false);

	const [definition, setDefinition] = useState(() => wrapDefinition(startDefinition));
	const extensions = useMemo<DesignerExtension[] | null>(() => {
		if (!externalEditorPlaceholder) {
			return null;
		}
		return [
			ReactExternalEditorProExtension.create({
				placeholder: externalEditorPlaceholder,
				rootEditor: <ExternalRootEditor />,
				stepEditor: <ExternalStepEditor />
			}),
			ReactExternalToolboxProExtension.create({
				controller: externalToolboxController
			}),
			ReactExternalControlBarProExtension.create({
				controller: externalControlBarController
			})
		];
	}, [externalEditorPlaceholder, externalControlBarController, externalToolboxController]);

	useEffect(() => {
		function sync() {
			setIsDragDisabled(externalControlBarController.isDragDisabled());
		}
		function onReady() {
			if (isDragDisabled) {
				externalControlBarController.setIsDragDisabled(true);
			}
		}
		externalControlBarController.onReady.subscribe(onReady);
		externalControlBarController.onStateChanged.subscribe(sync);
		return () => {
			externalControlBarController.onReady.unsubscribe(onReady);
			externalControlBarController.onStateChanged.unsubscribe(sync);
		};
	}, [externalControlBarController, isDragDisabled]);

	function zoomIn() {
		if (externalControlBarController.isReady()) {
			externalControlBarController.zoomIn();
		}
	}

	function zoomOut() {
		if (externalControlBarController.isReady()) {
			externalControlBarController.zoomOut();
		}
	}

	function toggleIsDragDisabled() {
		if (externalControlBarController.isReady()) {
			externalControlBarController.toggleIsDragDisabled();
		}
	}

	function toggleVisibilityClicked() {
		setIsVisible(!isVisible);
	}

	function onToolboxItemDragStarted(event: MouseEvent | TouchEvent) {
		event.preventDefault();
		if (!externalControlBarController.isReady()) {
			return;
		}

		const touchEvent = event as TouchEvent;
		const source = touchEvent.touches ? touchEvent.touches[0] : (event as MouseEvent);

		externalToolboxController.tryDrag(source.clientX, source.clientY, createStep());
	}

	return (
		<div className="page" data-testid="euc">
			<h2>External ui components</h2>

			<div className="control">
				<button onClick={zoomIn} className="btn">
					Zoom in
				</button>
				<button onClick={zoomOut} className="btn">
					Zoom out
				</button>
				<button onClick={toggleIsDragDisabled} className="btn">
					Drag & drop {isDragDisabled ? '❌' : '✅'}
				</button>
				<button onClick={toggleVisibilityClicked} className="btn">
					Toggle visibility
				</button>
				<button onMouseDown={onToolboxItemDragStarted} onTouchStart={onToolboxItemDragStarted} className="btn">
					&lt;toolboxItem&gt;
				</button>
			</div>

			{isVisible && extensions && (
				<SequentialWorkflowDesigner
					undoStackSize={10}
					definition={definition}
					onDefinitionChange={setDefinition}
					toolboxConfiguration={false}
					stepsConfiguration={{}}
					controlBar={false}
					rootEditor={false}
					stepEditor={false}
					extensions={extensions}
				/>
			)}

			<div className="external-editor">
				<div ref={setExternalEditorPlaceholder}></div>
			</div>
		</div>
	);
}
